import { useState } from 'react'
import { formatMoneyDocument } from '../../lib/format'
import { useProductGroups, useProducts, type ProductListItem, type UnitOfMeasure } from '../../features/products/api'
import type { PickedProduct } from '../../features/pos/api'
import { BoxIcon, GridIcon, ListIcon, PlusIcon } from './icons'

const VIEW_KEY = 'dpl.pos.view'

/** On a phone the shelf shows this many until asked for more, so the sale stays near. */
const PHONE_SHELF = 6

function rememberedView(): 'grid' | 'list' {
  try {
    return window.localStorage.getItem(VIEW_KEY) === 'list' ? 'list' : 'grid'
  } catch {
    return 'grid'
  }
}

/**
 * The shelf: every product for sale, by group, with what this shop has of it.
 *
 * Prices here are list prices, for finding things. What the customer pays — their
 * tier, a contract, a loyalty price — is the server's quote in the sale panel.
 */
export function ProductBrowser({
  locationId,
  search,
  pricesIncludeVat,
  onAdd,
}: {
  locationId: string
  search: string
  pricesIncludeVat: boolean
  onAdd: (product: PickedProduct) => void
}) {
  const groups = useProductGroups()
  const [groupId, setGroupId] = useState('')
  // Which products the cashier has switched to the box (D-210). Per product, not a mode for the
  // whole shelf: a mode left on is a mode somebody forgets, and twelve bulbs rung as twelve boxes
  // is a mistake found at the end of the day rather than at the counter. Kept while the till is
  // open so a customer buying three boxes of the same thing switches once.
  const [boxed, setBoxed] = useState<Record<string, boolean>>({})
  const [view, setView] = useState<'grid' | 'list'>(rememberedView)
  const [everything, setEverything] = useState(false)
  // Twenty seconds. The stock figure on a tile is the one thing another counter changes without
  // this one doing anything, and a cashier who is told six when there is one left finds out at the
  // worst moment — with a customer waiting. Only while the tab is in front: a till left open on a
  // back counter overnight should not poll until morning.
  const products = useProducts(
    { search, groupId: search ? '' : groupId, status: 'active', lowStock: false, page: 1, locationId },
    20_000,
  )
  const items = products.data?.items ?? []
  const activeGroups = (groups.data ?? []).filter((g) => g.isActive && g.productCount > 0).sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name))

  /** Whether this product is currently set to sell by the box, and whether it can be at all. */
  const asBox = (p: ProductListItem) => p.packDescription !== undefined && boxed[p.id] === true

  function switchUnit(p: ProductListItem, pack: boolean) {
    setBoxed((current) => ({ ...current, [p.id]: pack }))
  }

  function choose(next: 'grid' | 'list') {
    setView(next)
    try {
      window.localStorage.setItem(VIEW_KEY, next)
    } catch {
      // Only a preference.
    }
  }

  return (
    <section aria-label="Products" className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex min-w-0 flex-1 gap-2 overflow-x-auto pb-1" role="group" aria-label="Product groups">
          <Chip active={!groupId || !!search} onClick={() => setGroupId('')}>All</Chip>
          {activeGroups.map((g) => (
            <Chip key={g.id} active={!search && groupId === g.id} onClick={() => setGroupId(g.id)}>{g.name}</Chip>
          ))}
        </div>
        <button
          type="button"
          className="inline-flex shrink-0 items-center gap-1.5 rounded-md px-2 py-1.5 text-sm font-medium text-dpl-primary hover:bg-dpl-primary/5"
          onClick={() => choose(view === 'grid' ? 'list' : 'grid')}
        >
          {view === 'grid' ? <ListIcon className="h-4 w-4" /> : <GridIcon className="h-4 w-4" />}
          {view === 'grid' ? 'View as list' : 'View as grid'}
        </button>
      </div>

      {search && <p className="text-sm text-dpl-muted">{products.isFetching ? 'Searching…' : `${products.data?.total ?? 0} match “${search}” — Enter adds a barcode or code.`}</p>}

      {products.isLoading ? (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 2xl:grid-cols-4" aria-busy="true">
          {Array.from({ length: 8 }, (_, i) => <div key={i} className="h-52 animate-pulse rounded-lg bg-dpl-surface" />)}
        </div>
      ) : items.length === 0 ? (
        <p className="rounded-lg border border-dashed border-dpl-border bg-dpl-surface py-10 text-center text-sm text-dpl-muted">
          {search ? `Nothing matches “${search}”.` : 'No products in this group yet.'}
        </p>
      ) : view === 'grid' ? (
        <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3 2xl:grid-cols-4">
          {items.map((p, index) => (
            <li key={p.id} className={!everything && !search && index >= PHONE_SHELF ? 'hidden sm:block' : ''}>
              {/* The card is a div and the add is a button inside it, because the unit switch is a
                  button too and a button inside a button is neither valid nor reachable by keyboard.
                  `focus-within` keeps the ring the tile used to get from `focus-visible`. */}
              <div className="group flex h-full flex-col rounded-lg border border-dpl-border bg-dpl-surface shadow-sm transition focus-within:ring-2 focus-within:ring-dpl-primary hover:border-dpl-primary/40 hover:shadow">
                <button
                  type="button"
                  aria-label={asBox(p) ? `Add ${p.name} — ${p.packDescription}` : `Add ${p.name}`}
                  className="flex flex-1 flex-col p-3 text-left focus:outline-none"
                  onClick={() => onAdd(picked(p, asBox(p)))}
                >
                  <Picture product={p} className="h-24 w-full sm:h-28" />
                  <span className="mt-3 line-clamp-2 text-sm font-semibold leading-5">{p.name}</span>
                  <span className="text-xs text-dpl-muted">{p.code}</span>
                  {/* The price of what the tile will actually add. Showing the single price while a
                      box is chosen would be the one lie this toggle must not tell (D-210). */}
                  <span className="mt-2 block whitespace-nowrap text-base font-bold tabular-nums" title={pricesIncludeVat ? undefined : 'Before VAT'}>
                    {formatMoneyDocument(asBox(p) ? p.packPrice : p.retailPrice)}
                  </span>
                  {asBox(p) && (
                    <span className="text-xs text-dpl-muted">
                      a {p.packDescription?.toLowerCase()} · {formatMoneyDocument(p.packPrice / p.packSize)} each
                    </span>
                  )}
                  <span className="mt-auto flex items-center justify-between gap-2 pt-1">
                    <StockBadge product={p} />
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-dpl-primary/30 text-dpl-primary transition group-hover:bg-dpl-primary group-hover:text-white sm:h-9 sm:w-9">
                      <PlusIcon />
                    </span>
                  </span>
                </button>

                {p.packDescription && (
                  <UnitSwitch product={p} asPack={asBox(p)} onChoose={(pack) => switchUnit(p, pack)} />
                )}
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <ul className="divide-y divide-dpl-border rounded-lg border border-dpl-border bg-dpl-surface shadow-sm">
          {items.map((p) => (
            <li key={p.id} className="flex items-center gap-2 pr-3">
              <button
                type="button"
                aria-label={asBox(p) ? `Add ${p.name} — ${p.packDescription}` : `Add ${p.name}`}
                className="flex min-w-0 flex-1 items-center gap-3 px-3 py-2.5 text-left hover:bg-dpl-bg focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-dpl-primary"
                onClick={() => onAdd(picked(p, asBox(p)))}
              >
                <Picture product={p} className="h-10 w-10 shrink-0" />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium">{p.name}</span>
                  <span className="text-xs text-dpl-muted">{p.code}{p.barcode ? ` · ${p.barcode}` : ''}</span>
                </span>
                <StockBadge product={p} />
                <span className="w-24 text-right text-sm font-semibold tabular-nums">
                  {formatMoneyDocument(asBox(p) ? p.packPrice : p.retailPrice)}
                </span>
              </button>

              {p.packDescription && (
                <UnitSwitch product={p} asPack={asBox(p)} onChoose={(pack) => switchUnit(p, pack)} compact />
              )}
            </li>
          ))}
        </ul>
      )}

      {view === 'grid' && !everything && !search && items.length > PHONE_SHELF && (
        <button type="button" className="w-full rounded-lg border border-dpl-border bg-dpl-surface py-2.5 text-sm font-medium text-dpl-primary sm:hidden" onClick={() => setEverything(true)}>
          Show all {items.length} products
        </button>
      )}

      {(products.data?.total ?? 0) > items.length && (
        <p className="text-center text-xs text-dpl-muted">Showing {items.length} of {products.data?.total}. Search to find the rest.</p>
      )}
    </section>
  )
}

/**
 * Single or box, on the shelf, for a product the shop sells both ways (D-210).
 *
 * <b>Per product rather than a mode for the whole shelf.</b> A shelf-wide "selling boxes" switch is
 * one tap cheaper for a wholesale customer and one forgotten tap away from ringing twelve bulbs as
 * twelve boxes. This cannot be left on by accident: it says what it is beside the product it is
 * about, and the tile's price changes with it.
 *
 * The line in the basket has the same switch (D-204), which is where a mistake gets corrected. This
 * is where it is avoided.
 */
function UnitSwitch({
  product,
  asPack,
  onChoose,
  compact = false,
}: {
  product: ProductListItem
  asPack: boolean
  onChoose: (pack: boolean) => void
  compact?: boolean
}) {
  return (
    <span
      role="radiogroup"
      aria-label={`How ${product.name} is sold`}
      className={compact ? 'flex shrink-0 gap-1' : 'flex gap-1 border-t border-dpl-border px-3 py-2'}
    >
      {[false, true].map((pack) => (
        <button
          key={String(pack)}
          type="button"
          role="radio"
          aria-checked={asPack === pack}
          className={`rounded border px-2 py-1 text-xs font-medium transition ${
            asPack === pack
              ? 'border-dpl-primary bg-dpl-primary text-white'
              : 'border-dpl-border text-dpl-muted hover:border-dpl-primary/40'
          }`}
          onClick={() => onChoose(pack)}
        >
          {pack ? product.packDescription : 'Single'}
        </button>
      ))}
    </span>
  )
}

/** Recently added at this till, one tap from the basket again. */
export function RecentItems({ items, onAdd }: { items: PickedProduct[]; onAdd: (product: PickedProduct) => void }) {
  if (items.length === 0) return null
  return (
    <section aria-label="Recent items" className="rounded-lg border border-dpl-border bg-dpl-surface p-4 shadow-sm">
      <h2 className="mb-3 text-sm font-semibold">Recent items</h2>
      <ul className="grid grid-cols-1 gap-2 sm:grid-cols-2 2xl:grid-cols-4">
        {items.map((p) => (
          <li key={p.id}>
            <button
              type="button"
              aria-label={`Add ${p.name} again`}
              className="group flex w-full items-center gap-2.5 rounded-md border border-dpl-border p-2 text-left hover:border-dpl-primary/40"
              onClick={() => onAdd(p)}
            >
              <Picture product={p} className="h-9 w-9 shrink-0" />
              <span className="min-w-0 flex-1">
                <span className="block truncate text-xs font-medium">{p.name}</span>
                {p.retailPrice !== undefined && <span className="text-xs tabular-nums text-dpl-muted">{formatMoneyDocument(p.retailPrice)}</span>}
              </span>
              <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded border border-dpl-primary/30 text-dpl-primary group-hover:bg-dpl-primary group-hover:text-white">
                <PlusIcon className="h-4 w-4" />
              </span>
            </button>
          </li>
        ))}
      </ul>
    </section>
  )
}

/**
 * The product's primary photo, or a plain box where it has none. A remembered recent
 * item's signed link may have expired since; it falls back to the box too.
 */
export function Picture({ product, className }: { product: { name: string; imageUrl?: string }; className: string }) {
  const [failed, setFailed] = useState<string | null>(null)
  return product.imageUrl && failed !== product.imageUrl ? (
    <img src={product.imageUrl} alt="" loading="lazy" onError={() => setFailed(product.imageUrl ?? null)} className={`rounded-md bg-white object-contain ${className}`} />
  ) : (
    <span className={`items-center justify-center rounded-md bg-dpl-bg/70 text-dpl-muted/40 ${className.includes('hidden') ? '' : 'flex'} ${className}`}>
      <BoxIcon className="h-[45%] w-[45%]" />
    </span>
  )
}

function StockBadge({ product: p }: { product: ProductListItem }) {
  const count = stockCount(p.quantityAvailable, p.unitOfMeasure)
  const split = stockSplit(p)
  const [tone, text] = p.quantityAvailable <= 0
    ? ['bg-dpl-danger/10 text-dpl-danger', 'Out of stock']
    : p.belowReorderLevel
      ? ['bg-dpl-danger/10 text-dpl-danger', `Low: ${split ?? `${count} in stock`}`]
      : ['bg-dpl-success/10 text-dpl-success', split ?? `${count} in stock`]

  // What this shop can sell now: on hand, less what is held for orders. The title keeps the plain
  // total, because the split is the useful form on the shelf and the total is the useful one when
  // somebody is checking a figure against a count.
  return (
    <span
      title={split ? `${count} available here — ${split}` : `${count} available here`}
      className={`inline-block min-w-0 truncate whitespace-nowrap rounded px-2 py-0.5 text-xs font-medium tabular-nums ${tone}`}
    >
      {text}
    </span>
  )
}

/**
 * "carton" → "cartons", "box" → "boxes".
 *
 * Appending a bare "s" put "3 boxs" in front of every shop that calls its carton a box, which is
 * the commonest word for it. Only the sibilant ending needs the extra "e"; the labels this sees are
 * the handful a shop types into a product — carton, box, pack, case, bale.
 */
function plural(word: string): string {
  return /(s|x|z|ch|sh)$/i.test(word) ? `${word}es` : `${word}s`
}

/**
 * The shelf as it actually looks: whole cartons, and the loose pieces beside them (D-248).
 *
 * <b>Derived, never stored.</b> Stock is held in pieces and only in pieces — a carton is a way of
 * asking for sixty, not a second thing to count — so this is arithmetic on the one figure rather
 * than a second figure that could disagree with it. Which is also the answer to "does opening a
 * carton update the pieces?": it does, by itself. Sell four out of a full carton of sixty and the
 * sixty-piece total becomes fifty-six, which reads as fifty-six loose. Nobody has to tell the
 * system a box was opened, and there is no state to get wrong.
 *
 * Returns null where the product has no pack, so the badge keeps its old wording rather than
 * announcing "0 cartons" to a shop that does not sell that thing by the carton.
 */
function stockSplit(p: ProductListItem): string | null {
  const size = p.packSize

  if (!size || size <= 1 || p.quantityAvailable <= 0) {
    return null
  }

  // "Carton of 30" → "carton". The shop's own word for the box, whatever it chose.
  const label = (p.packDescription?.split(' ')[0] ?? 'Pack').toLowerCase()
  const whole = Math.floor(p.quantityAvailable / size)
  const loose = Math.round(p.quantityAvailable - (whole * size))

  if (whole === 0) {
    return `${loose} loose`
  }

  const cartons = `${whole} ${whole === 1 ? label : plural(label)}`
  return loose === 0 ? cartons : `${cartons} + ${loose}`
}

const units: Record<UnitOfMeasure, [string, string]> = { Piece: ['', ''], Metre: [' m', ' m'], Roll: [' roll', ' rolls'], Box: [' box', ' boxes'] }

/** "12", "40 m", "1 roll", "3 boxes" — a count as the shelf holds it. */
function stockCount(quantity: number, unit: UnitOfMeasure): string {
  const [one, many] = units[unit] ?? ['', '']
  return `${quantity.toLocaleString('en-KE', { maximumFractionDigits: 2 })}${quantity === 1 ? one : many}`
}

function Chip({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={`shrink-0 whitespace-nowrap rounded-full border px-4 py-1.5 text-sm font-medium transition ${
        active ? 'border-dpl-primary bg-dpl-primary text-white' : 'border-dpl-border bg-dpl-surface text-dpl-text hover:border-dpl-primary/40'
      }`}
    >
      {children}
    </button>
  )
}

function picked(p: ProductListItem, asPack = false): PickedProduct {
  return {
    id: p.id,
    code: p.code,
    name: p.name,
    retailPrice: p.retailPrice,
    imageUrl: p.imageUrl,
    available: p.quantityAvailable,
    // Carried only where the shop can actually sell a pack of it, so the till offers the choice on
    // the lines where it is real and on no others (D-204).
    packSize: p.packDescription ? p.packSize : undefined,
    packDescription: p.packDescription,
    packPrice: p.packDescription ? p.packPrice : undefined,
    // The unit the shelf was switched to. Never true for a product with no pack price, whatever a
    // stale bit of state might say (D-210).
    asPack: p.packDescription ? asPack : false,
  }
}
